import React, { useState, useRef, useEffect } from 'react'
import { OmDropdownMenu } from '@owma/kit'
import type { OmMenuItem } from '@owma/kit'
import checkIcon from 'assets/images/check-mark-16.svg'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'
import taskIcon from 'assets/images/todo/property-tasks.svg'
import classNames from 'classnames'
import i18n from 'i18n'
import SVG from 'react-inlinesvg'
import { TodoFolder } from '../../modules/TodoFolder'
import styles from './style.module.scss'

interface FolderSelectorProps {
  selectedFolder: TodoFolder | null
  folders: TodoFolder[]
  onFolderChange: (folder: TodoFolder | null) => void
  disabled?: boolean
  settingType?: string | null
  externalClassName?: string | null
}

const FolderSelector: React.FC<FolderSelectorProps> = ({
  selectedFolder,
  folders,
  onFolderChange,
  disabled,
  settingType = null,
  externalClassName = null,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const rootRef = useRef<HTMLDivElement>(null)

  // 브라우저 안쪽 외부 클릭(pointerdown)에서만 닫는다. 전역 blurHandler(window blur 시 .om-dropdown-open
  //   트리거를 click→닫음)와 Radix 자동 닫힘은 onOpenChange 에서 무시 → 브라우저 바깥 클릭으로는 안 닫힘
  //   (TimezonePicker 와 동일 패턴). 실제 닫힘은 이 pointerdown + 항목 선택으로만.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as HTMLElement | null
      if (target == null) return
      const inTrigger = rootRef.current?.contains(target) ?? false
      const inDropdown = target.closest('.om-dropdown') != null
      if (!inTrigger && !inDropdown) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [open])

  const getLabel = (folder: TodoFolder | null) => {
    return (
      <div className={styles.menuItem} style={{ gap: '8px' }}>
        <div className={styles.menuItemContent}>
          <div
            className={styles.colorDot}
            style={{
              backgroundColor: folder ? folder.calendarColor?._cdata || '#fd4c45' : 'transparent',
            }}
          />
          <div className={styles.folderInfo}>
            <div className={styles.folderName}>
              {folder ? folder.displayName : getNullFolderName()}
            </div>
            <div className={styles.folderOwner}>{folder?.owner || folder?.userInfo?.email}</div>
          </div>
        </div>
        {selectedFolder?.url === folder?.url ? (
          React.createElement(SVG as any, {
            src: checkIcon,
            alt: 'Selected',
            className: styles.checkIcon,
          })
        ) : (
          <div className={styles.checkIcon} />
        )}
      </div>
    )
  }

  const getFolderItems = (): OmMenuItem[] => {
    const folderItems =
      folders
        ?.filter((folder) => !folder.isReadOnly)
        .map((folder) => ({
          key: folder.url,
          label: getLabel(folder),
          onClick: () => {
            onFolderChange(folder)
            setOpen(false)
          },
        })) || []

    if (settingType !== null) {
      folderItems.unshift({
        key: 'lastFolderItem',
        label: getLabel(null),
        onClick: () => {
          onFolderChange(null)
          setOpen(false)
        },
      })
    }

    return folderItems
  }

  const getNullFolderName = (): string => {
    if (settingType === null) {
      return 'Tasks'
    }

    return settingType === 'todo'
      ? i18n.t('settings.calendarTabs.lastTaskAdded')
      : i18n.t('settings.calendarTabs.lastEventAdded')
  }

  /* Settings 에서 사용되는 컴포넌트 이므로 CSS 수정이 필요하면 Settings/Todo/style.module.scss 에도 수정이 필요할 수 있음. */
  return (
    <div ref={rootRef} className={classNames(styles.folderSelector, 'folderSelector')}>
      <div className={classNames(styles.titleIconWrapper, 'titleIconWrapper')}>
        {React.createElement(SVG as any, {
          src: taskIcon,
          className: styles.titleIcon,
          alt: 'tasks',
          style: { color: selectedFolder?.calendarColor?._cdata || '#fdff45' },
        })}
      </div>
      <OmDropdownMenu
        menu={{ items: getFolderItems() }}
        trigger={['click']}
        placement="bottomRight"
        getPopupContainer={() => document.body}
        disabled={disabled}
        overlayClassName={classNames(styles.dropdown, externalClassName || '')}
        open={open}
        onOpenChange={(next) => {
          // 열림만 반영, 닫힘 요청(전역 blurHandler 의 트리거 click·Radix 자동 닫힘)은 무시
          //   → 브라우저 바깥 클릭으로는 안 닫힘. 닫힘은 외부 pointerdown/항목 선택이 담당.
          if (next) setOpen(true)
        }}
      >
        <button
          className={classNames(styles.folderButton, 'folderButton', disabled ? styles.dimmed : '')}
          disabled={disabled}
        >
          <div className={styles.folderDetails}>
            <div className={styles.folderInfo}>
              <div className={classNames(styles.folderName, 'folderName')}>
                {selectedFolder?.displayName || getNullFolderName()}
              </div>
              <div className={styles.folderOwner}>
                <div className={classNames(styles.folderOwnerEmail, 'folderOwnerEmail')}>
                  {selectedFolder?.owner || selectedFolder?.userInfo?.email}
                </div>
              </div>
            </div>
          </div>
          <img src={chevronDownIcon} alt="More" className={styles.chevronIcon} />
        </button>
      </OmDropdownMenu>
    </div>
  )
}

export default FolderSelector
